import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import { useUser } from '@/hooks/use-user';
import {
  completedCountAtom,
  completionPercentageAtom,
} from '@/states/completion-percentage.state';
import { dealItemsStreamingState } from '@/states/products.state';
import { selectedRowsCountState } from '@/states/selected-rows-count.state';
import { useAtomValue } from 'jotai';

import { useDealContext } from '../../lib/contexts/deal.context';
import { useContainerQuery } from '../../lib/hooks/use-container-query';
import CompletionBar from './completion-bar';
import RelaunchUserTour from './relaunch-user-tour';
import StatusLabel from './status-label';
import ValidateBrand from './validate-brand';
import ValidateChoose from './validate-offer';
import { ValidationTicks } from './validation-ticks';

function Navigation() {
  const navRef = useRef<HTMLElement>(null);

  const { t } = useTranslation();
  const dealId = useDealContext().deal.id;
  const { pathname } = useLocation();
  const user = useUser();
  const isOfferLocked = useIsOfferLocked();

  const completedCount = useAtomValue(completedCountAtom);
  const totalCount = useAtomValue(selectedRowsCountState);
  const completionPercentage = useAtomValue(completionPercentageAtom);
  const isStreaming = useAtomValue(dealItemsStreamingState);
  const progressValue =
    completionPercentage !== null
      ? Math.min(Math.max(completionPercentage, 0), 100)
      : 0;

  const [containerRef, isIconOnly] = useContainerQuery<HTMLDivElement>(400);

  // const offerCompleted = totalCount > 0 && completedCount === totalCount;

  // Measure navigation height and expose it as a CSS var for sticky offsets (TableHeader).
  useEffect(() => {
    const measureHeight = () => {
      if (!navRef.current) return;
      const height = navRef.current.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--nav-height', `${height}px`);
    };

    measureHeight();

    const resizeObserver = new ResizeObserver(() => {
      measureHeight();
    });

    if (navRef.current) {
      resizeObserver.observe(navRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  if (!user) return null;

  return (
    <nav
      ref={navRef}
      data-component="Navigation"
      className={`flex items-center justify-between gap-4 border-b border-b-gray-100 px-10 py-4`}
    >
      <ul className={`flex items-center gap-3 text-sm font-medium`}>
        <li
          className={`transition-colors ${
            pathname.includes(`settings`) || pathname.includes(`history`)
              ? 'text-gray-400 hover:text-gray-900'
              : 'text-gray-900'
          }`}
        >
          <Link to={`/deal/${dealId}`} data-testid="products-button">
            {t('ui-v2.navigation.products-page')}
          </Link>
        </li>
        <li
          className={`transition-colors ${
            pathname.includes(`settings`)
              ? 'text-gray-900'
              : 'text-gray-400 hover:text-gray-900'
          }`}
        >
          <Link to={`/deal/${dealId}/settings`}>
            {t('ui-v2.navigation.settings-page')}
          </Link>
        </li>
        {user.isInternalUser && (
          <li
            className={`transition-colors ${
              pathname.includes(`history`)
                ? 'text-gray-900'
                : 'text-gray-400 hover:text-gray-900'
            }`}
          >
            <Link to={`/deal/${dealId}/history`}>
              {t('ui-v2.navigation.history-page')}
            </Link>
          </li>
        )}
      </ul>
      <aside className={`flex flex-1 items-center justify-end gap-4`}>
        <div
          className="flex flex-1 items-center justify-end gap-4"
          ref={containerRef}
        >
          {!user.isInternalUser &&
            !pathname.includes(`settings`) &&
            !isOfferLocked && <RelaunchUserTour />}
          <ValidationTicks iconOnly={isIconOnly} />
          <StatusLabel iconOnly={isIconOnly} />
        </div>
        <CompletionBar
          completedCount={completedCount}
          totalCount={totalCount}
          progressValue={progressValue}
          isStreaming={isStreaming}
        />
        {user.isInternalUser ? <ValidateChoose /> : <ValidateBrand />}
      </aside>
    </nav>
  );
}

export default Navigation;
